import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  SmartToy as BotIcon,
  Forum as ForumIcon,
  Logout as LogoutIcon,
  KeyboardDoubleArrowLeft as DoubleChevronLeftIcon,
  KeyboardDoubleArrowRight as DoubleChevronRightIcon,
  DocumentScanner as DocumentScannerIcon,
  ViewModule as ViewModuleIcon,
  Thermostat as ThermostatIcon,
  Psychology as PsychologyIcon,
  ExpandLess,
  ExpandMore,
  Science as ScienceIcon,
  Water as WaterIcon,
  Engineering as EngineeringIcon,
  ManageAccounts as ManageAccountsIcon,
  TextFields as TextFieldsIcon,
  Opacity as OpacityIcon,
  Campaign as CampaignIcon,
  LocalOffer as LocalOfferIcon,
  Help as HelpIcon,
  Analytics as AnalyticsIcon,
  Photo as PhotoIcon,
  Brightness3 as MoonIcon,
  MenuBook as MenuBookIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 65;

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const [dashboardSubmenuOpen, setDashboardSubmenuOpen] = useState(false);
  const [aiSubmenuOpen, setAiSubmenuOpen] = useState(false);
  const [managementSubmenuOpen, setManagementSubmenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setIsDrawerCollapsed(!isDrawerCollapsed);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const { userRoles } = useAuth();

  const isAdmin = userRoles.includes('Admin');
  
  const dashboardMenuItems = [
    { text: 'Main Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Active Users', icon: <PeopleIcon />, path: '/active-users' },
  ];

  const menuItems = [
    ...(isAdmin ? [
      { text: 'Users', icon: <PeopleIcon />, path: '/users' },
      { text: 'Photos', icon: <PhotoIcon />, path: '/photos' },
      { text: 'Stations', icon: <ThermostatIcon />, path: '/stations' },
      { text: 'Fishing Forecast', icon: <AnalyticsIcon />, path: '/biometrics' },
      { text: 'Moon Position', icon: <MoonIcon />, path: '/moon-position' },
      { text: 'Water Temps', icon: <OpacityIcon />, path: '/water-temps' },
    ] : []),
    { text: 'Lakes', icon: <WaterIcon />, path: '/lakes' },
    ...(isAdmin ? [
      { text: 'Announcements', icon: <CampaignIcon />, path: '/announcements' },
      { text: 'Offers', icon: <LocalOfferIcon />, path: '/offers' },
      { text: 'Help Context', icon: <HelpIcon />, path: '/help-context' },
      { text: 'Document Export', icon: <DocumentScannerIcon />, path: '/document-export' },
    ] : [])
  ];

  const aiMenuItems = [
    { text: 'Conversations', icon: <ChatIcon />, path: '/conversations' },
    { text: 'Turns', icon: <ForumIcon />, path: '/turns' },
    { text: 'Chat Bot', icon: <BotIcon />, path: '/chat' },
    { text: 'Test Chat', icon: <ScienceIcon />, path: '/test-chat' },
    { text: 'Hal', icon: <BotIcon />, path: '/hal' },
    { text: 'Knowledge Base', icon: <MenuBookIcon />, path: '/knowledge-base' },
    { text: 'VectorDB Search', icon: <StorageIcon />, path: '/vector-db-search' },
  ];

  const managementMenuItems = [
    { text: 'Agents', icon: <EngineeringIcon />, path: '/agents' },
    { text: 'Panels', icon: <ViewModuleIcon />, path: '/panels' },
    { text: 'LLM Prompts', icon: <TextFieldsIcon />, path: '/llm-prompts' },
  ];

  // Determine which menu items are visible for the current role
  const visibleDashboardMenuItems = isAdmin ? dashboardMenuItems : [];
  const visibleAiMenuItems = isAdmin ? aiMenuItems : [];
  const visibleManagementMenuItems = isAdmin ? managementMenuItems : [];

  // Check if current path is a submenu item
  const isDashboardSubmenuActive = visibleDashboardMenuItems.some(item => item.path === location.pathname);
  const isAiSubmenuActive = visibleAiMenuItems.some(item => item.path === location.pathname);
  const isManagementSubmenuActive = visibleManagementMenuItems.some(item => item.path === location.pathname);
  const isAiOrManagementActive = isAiSubmenuActive || isManagementSubmenuActive;

  const drawer = (
    <div>
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        py: 2,
        px: 2,
        gap: 1
      }}>
        <IconButton 
          onClick={handleDrawerCollapse}
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            padding: 0.5,
            borderRadius: 1,
            width: 'fit-content',
            p: 0.5,
            '&:hover': {
              backgroundColor: 'background.paper',
            },
          }}
          size="small"
        >
          {isDrawerCollapsed ? (
            <DoubleChevronRightIcon sx={{ width: 20, height: 20 }} />
          ) : (
            <DoubleChevronLeftIcon sx={{ width: 20, height: 20 }} />
          )}
        </IconButton>
        {!isDrawerCollapsed && (
          <img
            src={require('../assets/Logo2_Version1.png')}
            alt="Jimmy Houston Outdoors Logo"
            style={{
              width: '80%',
              height: 'auto',
              maxWidth: '150px',
              display: 'block'
            }}
          />
        )}
      </Toolbar>
      <Divider />
      <List>
        {/* Dashboard Menu Item with Submenu - Only show if user is admin */}
        {visibleDashboardMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={isDrawerCollapsed ? "Dashboard" : ""} placement="right">
                <ListItemButton
                  selected={isDashboardSubmenuActive}
                  onClick={() => !isDrawerCollapsed && setDashboardSubmenuOpen(!dashboardSubmenuOpen)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isDrawerCollapsed ? 'center' : 'initial',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: 0,
                    mr: isDrawerCollapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}>
                    <DashboardIcon />
                  </ListItemIcon>
                  {!isDrawerCollapsed && (
                    <>
                      <ListItemText primary="Dashboard" />
                      {dashboardSubmenuOpen ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            
            {/* Dashboard Submenu Items */}
            {!isDrawerCollapsed && (
              <Collapse 
                in={dashboardSubmenuOpen} 
                timeout="auto" 
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {visibleDashboardMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === item.path}
                        onClick={() => {
                          navigate(item.path);
                          setMobileOpen(false);
                        }}
                        sx={{
                          minHeight: 40,
                          pl: 4,
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 0,
                          mr: 3,
                          justifyContent: 'center',
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}
        
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={isDrawerCollapsed ? item.text : ""} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: isDrawerCollapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 0,
                  mr: isDrawerCollapsed ? 0 : 3,
                  justifyContent: 'center',
                }}>
                  {item.icon}
                </ListItemIcon>
                {!isDrawerCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
        
        {/* AI Menu Item with Hover Submenu - Only show if user has access to AI submenu items */}
        {visibleAiMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={isDrawerCollapsed ? "AI" : ""} placement="right">
                <ListItemButton
                  selected={isAiOrManagementActive}
                  onClick={() => !isDrawerCollapsed && setAiSubmenuOpen(!aiSubmenuOpen)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isDrawerCollapsed ? 'center' : 'initial',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: 0,
                    mr: isDrawerCollapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}>
                    <PsychologyIcon />
                  </ListItemIcon>
                  {!isDrawerCollapsed && (
                    <>
                      <ListItemText primary="AI" />
                      {aiSubmenuOpen ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            
            {/* AI Submenu Items */}
            {!isDrawerCollapsed && (
              <Collapse 
                in={aiSubmenuOpen} 
                timeout="auto" 
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {visibleAiMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === item.path}
                        onClick={() => {
                          navigate(item.path);
                          setMobileOpen(false);
                        }}
                        sx={{
                          minHeight: 40,
                          pl: 4,
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 0,
                          mr: 3,
                          justifyContent: 'center',
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  
                  {/* Management Nested Submenu within AI */}
                  {visibleManagementMenuItems.length > 0 && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={isManagementSubmenuActive}
                          onClick={() => setManagementSubmenuOpen(!managementSubmenuOpen)}
                          sx={{
                            minHeight: 40,
                            pl: 4,
                          }}
                        >
                          <ListItemIcon sx={{
                            minWidth: 0,
                            mr: 3,
                            justifyContent: 'center',
                          }}>
                            <ManageAccountsIcon />
                          </ListItemIcon>
                          <ListItemText primary="Management" />
                          {managementSubmenuOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>
                      
                      {/* Management Items - Nested within AI submenu */}
                      <Collapse 
                        in={managementSubmenuOpen} 
                        timeout="auto" 
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {visibleManagementMenuItems.map((item) => (
                            <ListItem key={item.text} disablePadding>
                              <ListItemButton
                                selected={location.pathname === item.path}
                                onClick={() => {
                                  navigate(item.path);
                                  setMobileOpen(false);
                                }}
                                sx={{
                                  minHeight: 40,
                                  pl: 6,
                                }}
                              >
                                <ListItemIcon sx={{
                                  minWidth: 0,
                                  mr: 3,
                                  justifyContent: 'center',
                                }}>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.text} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </>
                  )}
                </List>
              </Collapse>
            )}
          </>
        )}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <Tooltip title={isDrawerCollapsed ? "Logout" : ""} placement="right">
            <ListItemButton onClick={handleLogout} sx={{
              minHeight: 48,
              justifyContent: isDrawerCollapsed ? 'center' : 'initial',
              px: 2.5,
            }}>
              <ListItemIcon sx={{
                minWidth: 0,
                mr: isDrawerCollapsed ? 0 : 3,
                justifyContent: 'center',
              }}>
                <LogoutIcon />
              </ListItemIcon>
              {!isDrawerCollapsed && <ListItemText primary="Logout" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </div>
  );

  const currentDrawerWidth = isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, width: 'auto', minWidth: 'unset' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {dashboardMenuItems.find(item => item.path === location.pathname)?.text ||
             menuItems.find(item => item.path === location.pathname)?.text || 
             aiMenuItems.find(item => item.path === location.pathname)?.text ||
             managementMenuItems.find(item => item.path === location.pathname)?.text || 
             'Jimmy Houston Outdoors'}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              transition: theme => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: currentDrawerWidth,
              transition: theme => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default Layout;
